import posthog from "posthog-js";
import { POSTHOG_ENABLED } from "@/config/posthog";

type Primitive = string | number | boolean | null | undefined;

/** Flat, JSON-safe properties. Never put PII or free text in event props. */
export type AnalyticsProps = Record<string, Primitive>;

export type SignInFailureReason =
	| "email_not_confirmed"
	| "invalid_credentials"
	| "no_session"
	| "request_error"
	| "other";

export type AvmKind = "rent" | "sale";

/** Shared by checkout_started and (best-effort) checkout_completed. */
export interface CheckoutContext {
	plan_id: number;
	plan_name: string;
	billing_cycle: string;
	provider: string;
	source: string;
}

interface AvmEstimateProps {
	distrito: string;
	standalone: boolean;
}

interface PaywallProps {
	/** Stable id of the gate, independent of the (translated) copy. */
	gate_id: string;
	path: string;
}

export interface InsuranceQuoteFunnelProps {
	mortgage_id?: number | string;
	bank: string;
	currency: string;
}

export interface InsuranceQuoteStepProps {
	mortgage_id?: number | string;
	goals: string;
	goals_count: number;
}

export interface InsuranceQuoteAbandonedProps {
	mortgage_id?: number | string;
	step: "goals" | "contact";
	goals_count: number;
}

export interface InsuranceQuoteCompletedProps extends InsuranceQuoteFunnelProps {
	channel: "whatsapp" | "call";
	goals: string;
	goals_count: number;
}

type NoProps = Record<string, never>;

/**
 * Single source of truth for product events: name -> required properties.
 * Names follow `object_verb` in snake_case. Adding an event is one entry here.
 */
export interface AnalyticsEventMap {
	// Auth
	sign_up_completed: { method: "email" };
	sign_up_failed: { method: "email" };
	sign_in_completed: { method: "email" };
	sign_in_failed: { reason: SignInFailureReason };
	// Core product
	property_created: NoProps;
	property_details_saved: {
		property_id: number;
		distrito: string;
		has_coords: boolean;
	};
	mortgage_created: { property_id: number };
	amortization_added: { mortgage_id: number };
	mortgage_advanced_toggled: { open: boolean };
	rent_estimate_calculated: AvmEstimateProps;
	sale_estimate_calculated: AvmEstimateProps;
	estimate_saved: { kind: AvmKind; standalone: boolean };
	avm_report_downloaded: { kind: AvmKind };
	// Insurance quote funnel
	insurance_quote_banner_dismissed: { mortgage_id?: number | string };
	insurance_quote_opened: InsuranceQuoteFunnelProps;
	insurance_quote_goals_completed: InsuranceQuoteStepProps;
	insurance_quote_abandoned: InsuranceQuoteAbandonedProps;
	insurance_quote_completed: InsuranceQuoteCompletedProps;
	// Monetisation funnel
	paywall_viewed: PaywallProps;
	paywall_dismissed: PaywallProps;
	paywall_cta_clicked: PaywallProps;
	billing_cycle_toggled: { billing_cycle: string };
	checkout_started: CheckoutContext;
	checkout_completed: Pick<CheckoutContext, "provider"> &
		Partial<CheckoutContext>;
}

export type AnalyticsEvent = keyof AnalyticsEventMap;

/** Person properties attached to the event's user (PostHog $set / $set_once). */
export interface TrackOptions {
	set?: AnalyticsProps;
	setOnce?: AnalyticsProps;
}

/**
 * Sends a custom event to PostHog. A no-op when PostHog is disabled (local
 * dev, tests, missing token), so call sites never need to guard.
 */
export function track<E extends AnalyticsEvent>(
	event: E,
	props: AnalyticsEventMap[E],
	options?: TrackOptions,
): void {
	if (!POSTHOG_ENABLED) return;
	try {
		posthog.capture(event, {
			...props,
			...(options?.set && { $set: options.set }),
			...(options?.setOnce && { $set_once: options.setOnce }),
		});
	} catch {
		// Analytics must never break the app.
	}
}

/** Links the browser to a signed-in user. */
export function identifyUser(id: string, props?: AnalyticsProps): void {
	if (!POSTHOG_ENABLED) return;
	try {
		posthog.identify(id, props);
	} catch {
		// Analytics must never break the app.
	}
}

/** Forgets the current user (sign-out). Also clears super properties. */
export function resetAnalytics(): void {
	if (!POSTHOG_ENABLED) return;
	try {
		posthog.reset();
	} catch {
		// Analytics must never break the app.
	}
}

/** Properties attached to every later event from this browser. */
export function registerSuperProps(props: AnalyticsProps): void {
	if (!POSTHOG_ENABLED) return;
	try {
		posthog.register(props);
	} catch {
		// Analytics must never break the app.
	}
}

/** Updates properties on the identified person. */
export function setPersonProps(props: AnalyticsProps): void {
	if (!POSTHOG_ENABLED) return;
	try {
		posthog.setPersonProperties(props);
	} catch {
		// Analytics must never break the app.
	}
}

